import { Api, ordersApiUrl } from "@/services";
import { NearbyOrders } from "@/types";
import { useMutation, useQueries, UseQueryResult } from "@tanstack/react-query";
import { PropsWithChildren, createContext, useContext, useMemo, useState, useEffect } from "react";
import { useLocation } from "./Location.provider";
import { useToast } from "@/hooks/use-toast";
import { Location } from '../lib/location'
import { useSocket } from "@/lib/socket";
import { useAuth } from "@/hooks";
import { getErrorResponseMessage } from "@/lib";

type OrdersContextType = {
  nearbyOrdersQuery?: UseQueryResult<any, Error>
  activeOrderQuery?: UseQueryResult<any, Error>
  isLoading: boolean
  refetchAll(): void
  acceptOrder(orderId: string): Promise<any>
  acceptingOrder: string | null
  requestPickupPin: (orderId: string) => Promise<void>
  startDelivery: (orderId: string) => Promise<void>
  completeDelivery: ({ orderId, customerPin }: ICompleteDeliveryForm, clearInput: () => void) => Promise<void>
  completingDelivery: boolean;
  isAcceptingOrder: boolean;
}

type IStartDelivery = { currentLocation: Location; estimatedArrivalTime: number; orderId: string }

type ICompleteDelivery = {
  customerPin: string
  deliveryLocation: Location
  notes: string
  orderId: string
}

type ICompleteDeliveryForm = { customerPin: string; orderId: string }

const OrdersContext = createContext<OrdersContextType>({
  isLoading: false,
  refetchAll() { },
  acceptOrder: async (_orderId: string) => Promise.resolve({}),
  acceptingOrder: null,
  requestPickupPin: async (_orderId: string) => Promise.resolve(),
  startDelivery: async (_orderId: string) => Promise.resolve(),
  completeDelivery: async (_data: ICompleteDeliveryForm, _clearInput: () => void) => Promise.resolve(),
  completingDelivery: false,
  isAcceptingOrder: false
});

export function OrdersProvider({ children }: PropsWithChildren) {
  const { toast } = useToast()

  const { rider } = useAuth()

  const { isTracking, startTracking, getCurrentLocation } = useLocation()

  const [acceptingOrder, setAcceptingOrder] = useState<string | null>(null)

  const { socket, isConnected } = useSocket()

  useEffect(() => {
    if (isConnected && socket) {
      socket.on('order:new', handleNewOrder)
      socket.on('order:assigned', handleOrderAssigned)
      socket.on('order:cancelled', handleOrderCancelled)
      socket.on('order:updated', handleOrderUpdated)

      return () => {
        socket.off('order:new')
        socket.off('order:assigned')
        socket.off('order:cancelled')
        socket.off('order:updated')
      }
    }
  }, [isConnected, socket])

  const handleNewOrder = (order: any) => {
    nearbyOrdersQuery.refetch()
    toast({
      title: "New Order Available!",
      description: `₦${order.estimatedEarnings} - ${order.distance}km away`,
    })
  }

  const handleOrderAssigned = (data: any) => {
    if (data.riderId === rider?.id) {
      refetchAll()
    }
  }

  const handleOrderCancelled = (_data: any) => {
    refetchAll()
  }

  const handleOrderUpdated = (data: any) => {
    if (data.riderId === rider?.id && activeOrderQuery?.data?.id === data.orderId) {
      activeOrderQuery.refetch()
    }
  }

  const queries = useQueries({
    queries: [
      {
        queryKey: ["nearby-orders"],
        queryFn: async () => (await Api.get(ordersApiUrl.nearbyOrders)).data?.data,
      },
      {
        queryKey: ["active-order"],
        queryFn: async () => (await Api.get(ordersApiUrl.activeOrders)).data?.data?.deliveries,
      }
    ]
  })

  const acceptOrderMutation = useMutation({
    mutationFn: async (orderId: string) => (await Api.post(ordersApiUrl.acceptOrder(orderId))).data,
  })

  const requestPickupPinMutation = useMutation({
    mutationFn: async (orderId: string) => (await Api.post(ordersApiUrl.requestPickupPin(orderId))).data,
  })

  const startDeliveryMutation = useMutation({
    mutationFn: async ({ currentLocation, estimatedArrivalTime, orderId }: IStartDelivery) => (await Api.post(ordersApiUrl.startDelivery(orderId), { currentLocation, estimatedArrivalTime })).data,
  })

  const completeDeliveryMutation = useMutation({
    mutationFn: async ({ customerPin, deliveryLocation, orderId, notes }: ICompleteDelivery) => (await Api.post(ordersApiUrl.completeDelivery(orderId), { customerPin, deliveryLocation, notes })).data,
  })

  const acceptOrder = async (orderId: string) => {
    await acceptOrderMutation.mutateAsync(orderId, {
      onError(error: any){
        toast({
          title: "Failed to Accept Order",
          description: getErrorResponseMessage(error),
          variant: "destructive"
        })
      },
      onSuccess(data){
        setAcceptingOrder(data?.data?.orderId)
        toast({
          title: "Order Accepted!",
          description: data?.nextStep || "Order has been assigned to you",
        })
        refetchAll()
        if (!isTracking) {
          startTracking()
        }
      }
    })
  }

  const requestPickupPin = async (orderId: string) => {
    await requestPickupPinMutation.mutateAsync(orderId, {
      onError(error: any){
        toast({
          title: "Failed to Generate PIN",
          description: getErrorResponseMessage(error),
          variant: "destructive"
        })
      },
      onSuccess(){
        refetchAll()
        toast({
          title: "Pickup PIN Generated",
          description: "PIN has been sent to the vendor",
        })
      }
    })
  }

  const startDelivery = async (orderId: string) => {
    const location = await getCurrentLocation()
    const data: IStartDelivery = {
      currentLocation: location,
      estimatedArrivalTime: 15,
      orderId
    }
    await startDeliveryMutation.mutateAsync(data, {
      onError(error: any){
        toast({
          title: "Failed to Start Delivery",
          description: getErrorResponseMessage(error),
          variant: "destructive"
        })
      },
      onSuccess(){
        refetchAll()
        toast({
          title: "Delivery Started!",
          description: "Customer has been notified with delivery PIN",
        })
      }
    })
  }

  const completeDelivery = async ({ orderId, customerPin }: ICompleteDeliveryForm, clearInput: () => void) => {
    if (!orderId || !customerPin) return

    const location = await getCurrentLocation()

    const data: ICompleteDelivery = {
      deliveryLocation: location,
      customerPin,
      notes: 'Delivered successfully',
      orderId
    }

    await completeDeliveryMutation.mutateAsync(data, {
      onSuccess(data){
        toast({
          title: "Delivery Completed!",
          description: `You earned ₦${data?.payouts?.riderPayout?.netAmount || 0}`,
        })
        clearInput()
      },
      onError(error: any){
        toast({
          title: "Delivery Failed",
          description: getErrorResponseMessage(error),
          variant: "destructive"
        })
      }
    })
  }

  const [nearbyOrdersQuery, activeOrderQuery] = queries
  const isLoading = useMemo(() => (queries.some(query => query.isLoading)), [queries])

  function refetchAll() {
    queries.forEach(query => query.refetch())
  }

  return (
    <OrdersContext.Provider value={{
      nearbyOrdersQuery,
      activeOrderQuery,
      isLoading,
      refetchAll,
      acceptOrder,
      acceptingOrder,
      requestPickupPin,
      startDelivery,
      completeDelivery,
      completingDelivery: completeDeliveryMutation.isPending,
      isAcceptingOrder: acceptOrderMutation.isPending,
    }}>
      {children}
    </OrdersContext.Provider>
  )
}

export function useOrders() {
  const context = useContext(OrdersContext);
  if (!context) {
    throw new Error("useOrders must be used within an OrdersProvider");
  }
  return context;
}